import React, { useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import PageTransition from "./PageTransition";
import Logo from "./Logo";
import Avatar from "./Avatar";
import { useTheme } from "../context/ThemeContext";
import ThemeToggle from "./ThemeToggle";
import {
  getUserInfo,
  logout,
  hasPermission,
  canManageUsers,
  canManageOrders,
  canViewDashboard,
  canManageMedicines,
  canManagePatients,
  getAccessLevel
} from "../services/auth";

// Icons for the sidebar navigation
const DashboardIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 4H10V10H4V4Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14 4H20V10H14V4Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4 14H10V20H4V14Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14 14H20V20H14V14Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PatientsIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
    <circle cx="9" cy="7" r="4"></circle>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
  </svg>
);

const MedicinesIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>
    <path d="M12 5 8.5 9.5"></path>
    <path d="M16 9 12 13"></path>
  </svg>
);

const PharmaciesIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3 21H21M5 21V7L12 3L19 7V21M9 21V15H15V21"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ProvidersIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle
      cx="12"
      cy="7"
      r="4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 11v4M10 13h4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StatesIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="2" />
    <path
      d="M12 4C14.6522 4 17.1957 5.05357 19.0711 6.92893C20.9464 8.8043 22 11.3478 22 14"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M4 12H12M12 12H20M12 12V4M12 12V20"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const SettingsIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.0113 9.77251C4.28059 9.5799 4.48572 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15V15Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const OrdersIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5C15 6.10457 14.1046 7 13 7H11C9.89543 7 9 6.10457 9 5Z"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path
      d="M9 12H15"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M9 16H15"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const ReceiptsIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M20 10V7C20 5.89543 19.1046 5 18 5H6C4.89543 5 4 5.89543 4 7V10"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M4 14V17C4 18.1046 4.89543 19 6 19H18C19.1046 19 20 18.1046 20 17V14"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <rect
      x="3"
      y="10"
      width="18"
      height="4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M8 14H16"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M8 10V14"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M16 10V14"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const CreateOrderIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5C15 6.10457 14.1046 7 13 7H11C9.89543 7 9 6.10457 9 5Z"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path
      d="M12 11V17"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M9 14H15"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const LogoutIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M16 17L21 12L16 7"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M21 12H9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Simple open/close arrow icons for sidebar toggle
const SidebarToggleIcon = ({ isOpen }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {isOpen ? (
      // Two arrows pointing left (close)
      <>
        <path
          d="M19 12H5"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12 5L5 12L12 19"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ) : (
      // Two arrows pointing right (open)
      <>
        <path
          d="M5 12H19"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12 5L19 12L12 19"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    )}
  </svg>
);

const LayoutContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const Navbar = styled.header`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background-color: var(--header-bg);
  border-bottom: 1px solid var(--border-color);
  transition: background-color 0.3s ease, border-color 0.3s ease;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: 70px;
  box-sizing: border-box;
`;

const NavbarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex: 1;
  margin-top: 70px;
  padding-left: ${(props) => (props.isMinimized ? "60px" : "250px")};
  transition: padding-left 0.3s ease;
`;

const Sidebar = styled.aside`
  width: ${(props) => (props.isMinimized ? "60px" : "250px")};
  background-color: var(--sidebar-bg);
  border-right: 1px solid var(--border-color);
  padding: 1.5rem 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: width 0.3s ease, background-color 0.3s ease,
    border-color 0.3s ease;
  overflow: visible;
  position: fixed;
  top: 70px;
  left: 0;
  height: calc(100vh - 70px);
  z-index: 100;
`;

const MainContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background-color: var(--header-bg);
  border-bottom: 1px solid var(--border-color);
  transition: background-color 0.3s ease, border-color 0.3s ease;
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
`;

// Toggle button for sidebar
const SidebarToggleButton = styled.button`
  position: absolute;
  top: 1rem;
  right: -12px;
  background-color: var(--primary-color);
  border: 2px solid var(--card-bg);
  border-radius: 6px;
  color: white;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  z-index: 9999;
  min-width: 32px;
  min-height: 32px;

  &:hover {
    background-color: var(--primary-dark);
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
  }

  &:active {
    transform: scale(0.95);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const UserName = styled.div`
  text-align: right;
  font-weight: 500;
  color: var(--text-primary);
`;

const RoleBadge = styled.span`
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--primary-color);
  background-color: var(--gray-100);
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  margin-left: 0.5rem;
`;

const MainContent = styled.main`
  flex: 1;
  padding: 2rem;
  background-color: var(--bg-secondary);
  overflow-y: auto;
  transition: background-color 0.3s ease;
`;

const NavMenu = styled.nav`
  margin-top: 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const NavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.isMinimized ? "center" : "flex-start")};
  padding: 0.75rem 1rem;
  color: var(--text-secondary);
  font-weight: 500;
  transition: all 0.2s ease;
  margin-bottom: 0.5rem;
  width: ${(props) => (props.isMinimized ? "90%" : "90%")};
  border-radius: var(--border-radius);
  text-decoration: none;

  svg {
    margin-right: ${(props) => (props.isMinimized ? "0" : "0.75rem")};
    opacity: 0.75;
    transition: opacity 0.2s ease;
  }

  &:hover {
    background-color: var(--gray-100);
    color: var(--text-primary);
    text-decoration: none;

    svg {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(1px);
    box-shadow: none;
  }

  &.active {
    background-color: var(--primary-dark);
    color: white;
    font-weight: 600;

    svg {
      opacity: 1;
    }
  }
`;

const NavItemText = styled.span`
  display: ${(props) => (props.isMinimized ? "none" : "inline")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CreateOrderButton = styled(NavLink)`
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.isMinimized ? "center" : "center")};
  padding: 0.75rem 1rem;
  margin-top: 1rem;
  width: 90%;
  background-color: var(--primary-color);
  color: white;
  font-weight: 600;
  border-radius: var(--border-radius);
  transition: all 0.2s ease;
  text-decoration: none;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  svg {
    margin-right: ${(props) => (props.isMinimized ? "0" : "0.75rem")};
  }

  &:hover {
    background-color: var(--primary-light);
    transform: translateY(-1px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    text-decoration: none;
  }

  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
`;

const CreateOrderText = styled.span`
  display: ${(props) => (props.isMinimized ? "none" : "inline")};
`;

const BottomNavItems = styled.div`
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const NavbarLogoWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: 25px;
  margin-left: 0px;
`;

const SidebarLogoWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.isMinimized ? "center" : "flex-start")};
  padding: ${(props) => (props.isMinimized ? "0 0.5rem" : "0 1.25rem")};
  position: relative;
  margin-bottom: 1rem;

  ${(props) =>
    props.isMinimized &&
    `
    transform: scale(0.6);
    margin: -12px 0 0 0;
  `}
`;

function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const userInfo = getUserInfo();
  const accessLevel = getAccessLevel();
  const { darkMode } = useTheme();
  const [isMinimized, setIsMinimized] = useState(false);

  // Handle sidebar toggle
  const handleToggleSidebar = () => {
    setIsMinimized(!isMinimized);
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Get page title based on current route
  const getPageTitle = () => {
    const pathname = location.pathname;
    if (pathname === "/") return "Dashboard";
    if (pathname.startsWith("/patients")) return "Patients";
    if (pathname.startsWith("/orders/create")) return "Create Order Request";
    if (pathname.startsWith("/orders")) return "Order Requests";
    if (pathname.startsWith("/surveys")) return "Surveys";
    if (pathname.startsWith("/medicines/manage")) return "Medicine Management";
    if (pathname.startsWith("/medicines")) return "Medicines";
    if (pathname.startsWith("/pharmacies/manage")) return "Pharmacy Management";
    if (pathname.startsWith("/pharmacies")) return "Pharmacies";
    if (pathname.startsWith("/providers")) return "Providers";
    if (pathname.startsWith("/states")) return "States";
    if (pathname.startsWith("/receipts")) return "Receipts";
    if (pathname.startsWith("/settings")) return "Settings";

    return "Medical System";
  };

  return (
    <LayoutContainer>
      <Navbar>
        <NavbarLeft>
          <NavbarLogoWrapper>
            <Logo minimized={false} />
          </NavbarLogoWrapper>
        </NavbarLeft>
        <UserProfile>
          <UserName>
            {userInfo?.email || "User"} <RoleBadge>{accessLevel}</RoleBadge>
          </UserName>
          <ThemeToggle />
          <Avatar name={userInfo?.email || "User"} size={36} />
        </UserProfile>
      </Navbar>
      
      <ContentWrapper  isMinimized={isMinimized}>
        <Sidebar isMinimized={isMinimized}>
          <SidebarToggleButton
            onClick={handleToggleSidebar}
            title={isMinimized ? "Expand Sidebar" : "Collapse Sidebar"}
          >
            <SidebarToggleIcon isOpen={!isMinimized} />
          </SidebarToggleButton>
          <NavMenu>
            <NavItem to="/" end isMinimized={isMinimized}>
              <DashboardIcon />
              <NavItemText isMinimized={isMinimized}>Dashboard</NavItemText>
            </NavItem>

            <NavItem to="/patients" isMinimized={isMinimized}>
              <PatientsIcon />
              <NavItemText isMinimized={isMinimized}>Patients</NavItemText>
            </NavItem>

            <NavItem to="/orders" isMinimized={isMinimized}>
              <OrdersIcon />
              <NavItemText isMinimized={isMinimized}>Order Requests</NavItemText>
            </NavItem>

            <NavItem to="/surveys" isMinimized={isMinimized}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11 19H5C3.89543 19 3 18.1046 3 17V7C3 5.89543 3.89543 5 5 5H19C20.1046 5 21 5.89543 21 7V11"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M16 11H8"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M12 15H8"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M8 7H8.01"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <circle
                  cx="17"
                  cy="17"
                  r="4"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M17 14.5V17"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M17 17H18.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              <NavItemText isMinimized={isMinimized}>Surveys</NavItemText>
            </NavItem>

            <NavItem to="/states" isMinimized={isMinimized}>
              <StatesIcon />
              <NavItemText isMinimized={isMinimized}>States</NavItemText>
            </NavItem>

            <NavItem to="/pharmacies" isMinimized={isMinimized}>
              <PharmaciesIcon />
              <NavItemText isMinimized={isMinimized}>Pharmacies</NavItemText>
            </NavItem>

            <NavItem to="/providers" isMinimized={isMinimized}>
              <ProvidersIcon />
              <NavItemText isMinimized={isMinimized}>Providers</NavItemText>
            </NavItem>

            <NavItem to="/medicines" isMinimized={isMinimized}>
              <MedicinesIcon />
              <NavItemText isMinimized={isMinimized}>Medicines</NavItemText>
            </NavItem>

            <NavItem to="/receipts" isMinimized={isMinimized}>
              <ReceiptsIcon />
              <NavItemText isMinimized={isMinimized}>Receipts</NavItemText>
            </NavItem>

            <CreateOrderButton to="/orders/create" isMinimized={isMinimized}>
              <CreateOrderIcon />
              <CreateOrderText isMinimized={isMinimized}>
                Create Order Request
              </CreateOrderText>
            </CreateOrderButton>
          </NavMenu>
          <BottomNavItems>
            <NavItem to="/settings" isMinimized={isMinimized}>
              <SettingsIcon />
              <NavItemText isMinimized={isMinimized}>Settings</NavItemText>
            </NavItem>
            <NavItem to="#" onClick={handleLogout} isMinimized={isMinimized}>
              <LogoutIcon />
              <NavItemText isMinimized={isMinimized}>Log Out</NavItemText>
            </NavItem>
          </BottomNavItems>
        </Sidebar>
        
        <MainContentWrapper>
          <MainContent>
            <PageTransition>
              <Outlet />
            </PageTransition>
          </MainContent>
        </MainContentWrapper>
      </ContentWrapper>
    </LayoutContainer>
  );
}

export default Layout;
